import { createCanvas, loadImage, CustomCanvas as Canvas, CustomImage as Image } from "../customCanvas";
import { arrayBufferToDataUrl, getMimeTypeOfArrayBuffer } from "./crossplatform";
// Use cross-platform version of "mini" file. Also the default export doesn't work for some reason.
import { orientation as exifOrientation } from "exifr/dist/mini.umd.cjs";

/**
 * Logic for scaling and orienting images.
 * Much of this file is taken literally or spiritually from
 * the Blueimp image load library.
 */

let supportsAutoOrientation: boolean | null = null;

/*
Get EXIF orientation value.
Exif orientation values to correctly display the letter F:

    1             2
  ██████        ██████
  ██                ██
  ████            ████
  ██                ██
  ██                ██

    3             4
      ██        ██
      ██        ██
    ████        ████
      ██        ██
  ██████        ██████

    5             6
██████████    ██
██  ██        ██  ██
██            ██████████

    7             8
        ██    ██████████
    ██  ██        ██  ██
██████████            ██

*/

export const getExifOrientation = (arrayBuffer: ArrayBuffer): Promise<number> => {
    return new Promise((resolve) => {
        const mimeType = getMimeTypeOfArrayBuffer(arrayBuffer);
        if (mimeType !== "image/jpeg") {
            return resolve(1);
        }
        exifOrientation(arrayBuffer)
            .then((orientation: number | undefined) => {
                if (!orientation) return resolve(1); // No EXIF data.
                return resolve(orientation);
            })
            .catch(() => {
                return resolve(1);
            });
    });
};

// Whether the execution environment lacks automatic image orientation.
// Checks whether the test URL with EXIF orientation gets oriented correctly.
const requiresCanvasOrientation = async (): Promise<boolean> => {
    if (supportsAutoOrientation !== null) {
        return !supportsAutoOrientation;
    }
    // black+white 3x2 JPEG, with the following meta information set:
    // - EXIF Orientation: 6 (Rotated 90° CCW)
    // Image data layout (B=black, F=white):
    // BFF
    // BBB
    const testImageUrl =
        "data:image/jpeg;base64,/9j/4QAiRXhpZgAATU0AKgAAAAgAAQESAAMAAAABAAYAAAA" +
        "AAAD/2wCEAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBA" +
        "QEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQE" +
        "BAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAf/AABEIAAIAAwMBEQACEQEDEQH/x" +
        "ABRAAEAAAAAAAAAAAAAAAAAAAAKEAEBAQADAQEAAAAAAAAAAAAGBQQDCAkCBwEBAAAAAAA" +
        "AAAAAAAAAAAAAABEBAAAAAAAAAAAAAAAAAAAAAP/aAAwDAQACEQMRAD8AG8T9NfSMEVMhQ" +
        "voP3fFiRZ+MTHDifa/95OFSZU5OzRzxkyejv8ciEfhSceSXGjS8eSdLnZc2HDm4M3BxcXw" +
        "H/9k=";
    const img = await loadImage(testImageUrl);
    supportsAutoOrientation = img.width === 2 && img.height === 3;
    return !supportsAutoOrientation;
};

// Width and height to use for the canvas.
export const canvasDims = (img: Image): [number, number] => {
    const maxDimension = 8192;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    if (img.naturalHeight <= maxDimension && img.naturalWidth <= maxDimension) {
        return [img.naturalWidth, img.naturalHeight];
    }
    if (img.naturalHeight > img.naturalWidth) {
        return [aspectRatio * maxDimension, maxDimension];
    }
    return [maxDimension, maxDimension / aspectRatio];
};

const getScaledAndOrientedCanvas = async (arrayBuffer: ArrayBuffer): Promise<Canvas> => {
    const dataUrl = await arrayBufferToDataUrl(arrayBuffer);
    const img = await loadImage(dataUrl);
    const [canvasWidth, canvasHeight] = canvasDims(img);
    const needToOrient = await requiresCanvasOrientation();
    if (!needToOrient) {
        const canvas = createCanvas(canvasWidth, canvasHeight);
        canvas.getContext("2d")?.drawImage(img, 0, 0, canvasWidth, canvasHeight);
        return canvas;
    }
    const orientation = await getExifOrientation(arrayBuffer);
    const [orientedWidth, orientedHeight] = orientation > 4 ? [canvasHeight, canvasWidth] : [canvasWidth, canvasHeight];
    const canvas = createCanvas(orientedWidth, orientedHeight);
    const ctx = canvas.getContext("2d")!;

    // Orient the canvas.
    switch (orientation) {
        case 2:
            ctx.translate(orientedWidth, 0);
            ctx.scale(-1, 1);
            break;
        case 3:
            ctx.translate(orientedWidth, orientedHeight);
            ctx.rotate(Math.PI);
            break;
        case 4:
            ctx.translate(0, orientedHeight);
            ctx.scale(1, -1);
            break;
        case 5:
            ctx.rotate(-0.5 * Math.PI);
            ctx.scale(-1, 1);
            break;
        case 6:
            ctx.rotate(-0.5 * Math.PI);
            ctx.translate(-orientedWidth, 0);
            break;
        case 7:
            ctx.rotate(-0.5 * Math.PI);
            ctx.translate(-orientedWidth, orientedHeight);
            ctx.scale(1, -1);
            break;
        case 8:
            ctx.rotate(0.5 * Math.PI);
            ctx.translate(0, -orientedHeight);
            break;
        default:
            break;
    }

    ctx.drawImage(img, 0, 0, orientedWidth, orientedHeight);
    return canvas;
};

export const getAttributesFromArrayBuffer = async (arrayBuffer: ArrayBuffer) => {
    const canvas = await getScaledAndOrientedCanvas(arrayBuffer);
    const mimeTypeData = getMimeTypeOfArrayBuffer(arrayBuffer);
    if (mimeTypeData === "image/jpeg") {
        return {
            dataUrl: canvas.toDataURL("image/jpeg", 1.0),
            height: canvas.height,
            width: canvas.width,
        };
    }
    return {
        dataUrl: canvas.toDataURL(),
        height: canvas.height,
        width: canvas.width,
    };
};
